import {
  createMediator,
  type Mediator,
  type MediatorTransitionResult,
  type TypedEvent,
} from "@/shared/lib/componentHierarchy";

interface ModalEntry {
  id: number;
  depth: number;
  close: () => void;
  lockScroll: boolean;
}
interface ModalStackState {
  entries: readonly ModalEntry[];
  nextId: number;
}
export type ModalStackEvent =
  | { type: "modal-stack.register"; entry: ModalEntry }
  | { type: "modal-stack.unregister"; id: number }
  | { type: "modal-stack.close-all" }
  | { type: "modal-stack.reset" }
  | { type: "modal-stack.escape"; isComposing: boolean };
export type ModalStackEffect =
  | { type: "scroll-lock"; locked: boolean }
  | { type: "close"; close: () => void };

export function isModalStackEvent(event: TypedEvent): event is ModalStackEvent {
  return [
    "modal-stack.register",
    "modal-stack.unregister",
    "modal-stack.close-all",
    "modal-stack.reset",
    "modal-stack.escape",
  ].includes(event.type);
}

function transitionModalStack(
  state: ModalStackState,
  event: ModalStackEvent,
): MediatorTransitionResult<ModalStackState, ModalStackEffect> {
  switch (event.type) {
    case "modal-stack.register": {
      const entries = [...state.entries, event.entry];
      return {
        status: "handled",
        state: { entries, nextId: event.entry.id + 1 },
        effects: [
          {
            type: "scroll-lock",
            locked: entries.some((entry) => entry.lockScroll),
          },
        ],
      };
    }
    case "modal-stack.unregister": {
      const entries = state.entries.filter((entry) => entry.id !== event.id);
      return {
        status: "handled",
        state: { ...state, entries },
        effects: [
          {
            type: "scroll-lock",
            locked: entries.some((entry) => entry.lockScroll),
          },
        ],
      };
    }
    case "modal-stack.reset":
    case "modal-stack.close-all":
      return {
        status: "handled",
        state: { ...state, entries: [] },
        effects: [
          { type: "scroll-lock", locked: false },
          ...(event.type === "modal-stack.close-all"
            ? [...state.entries].reverse().map(
                (entry): ModalStackEffect => ({
                  type: "close",
                  close: entry.close,
                }),
              )
            : []),
        ],
      };
    case "modal-stack.escape": {
      const topmost = state.entries.at(-1);
      return event.isComposing || !topmost
        ? { status: "unhandled" }
        : {
            status: "handled",
            state,
            effects: [{ type: "close", close: topmost.close }],
          };
    }
  }
}

export function createModalStackMediator(
  runEffect: (effect: ModalStackEffect) => void,
): Mediator<ModalStackState, ModalStackEvent, ModalStackEffect> {
  return createMediator<ModalStackState, ModalStackEvent, ModalStackEffect>(
    { entries: [], nextId: 0 },
    transitionModalStack,
    { runEffect },
  );
}
